import { For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { AssignRowScope, CompanyUserDetailScope } from 'mormaii-web-presentation/company-user-detail'

import { Button, ChoiceFilter, FormField, TextFilter } from '../../../../components/filters'
import { ErrorNotice, Loading, Panel } from '../../../../components/ui'

import Css from './company-user-detail.module.scss'

/**
 * A ficha de um usuario da empresa.
 *
 * Substituiu dois dialogos - corrigir CPF e atribuir colaboradores. Eram duas portas
 * para o mesmo assunto, e cada uma mostrava um pedaco: quem abrisse "editar" via um
 * campo de CPF e concluia, com razao, que era so isso que dava para configurar.
 *
 * Quatro secoes, na ordem em que se pensa nelas: quem e, o que se corrige, o que ela
 * acompanha, e o que se faz com a conta.
 */
export function CompanyUserDetailView(props: ViewProps<CompanyUserDetailScope>): JSX.Element {
    return (
        <Show when={!props.scope.loading || props.scope.name !== ''} fallback={<Loading />}>
            <Show when={!props.scope.error} fallback={<ErrorNotice message={props.scope.error!} />}>
                <div class={Css.screen}>
                    <button type="button" onClick={() => void props.scope.onBack()} class={Css.back}>
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="m15 18-6-6 6-6" />
                        </svg>
                        {props.scope.backLabel}
                    </button>

                    <Identification scope={props.scope} />
                    <Form scope={props.scope} />

                    <Show when={props.scope.showAssignment}>
                        <Assignment scope={props.scope} />
                    </Show>

                    <Account scope={props.scope} />
                </div>
            </Show>
        </Show>
    )
}

/** Quem e a pessoa - e o que NAO se edita: o e-mail e o papel. */
function Identification(props: { scope: CompanyUserDetailScope }): JSX.Element {
    const initials = () =>
        props.scope.name
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() ?? '')
            .join('')

    return (
        <Panel>
            <div class={Css.identity}>
                <span class={Css.initials}>{initials()}</span>

                <div class={Css.texts}>
                    <div class={Css.nameRow}>
                        <h2 class={Css.name}>{props.scope.name}</h2>
                        <span class={Css.status} classList={{ [Css.active]: props.scope.active }}>
                            {props.scope.active ? 'Ativo' : 'Sem acesso'}
                        </span>
                    </div>

                    {/*
                        O e-mail e o papel ficam AQUI, fora do formulario, porque nao
                        se editam: o e-mail e a chave de login - troca-lo em outra
                        pessoa e tomar a conta dela - e o papel vem do cadastro.
                    */}
                    <p class={Css.email}>{props.scope.email}</p>
                    <p class={Css.role}>{props.scope.papel || 'Sem papel definido'}</p>
                </div>
            </div>
        </Panel>
    )
}

function Form(props: { scope: CompanyUserDetailScope }): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSave()
    }

    return (
        <form onSubmit={submit}>
            <Panel title="Dados da pessoa">
                <div class={Css.panel}>
                    <div class={Css.grid}>
                        <FormField
                            label="Nome"
                            value={props.scope.firstName}
                            error={props.scope.firstNameError}
                            onChange={(value) => props.scope.onFirstNameChanged(value)}
                        />
                        <FormField
                            label="Sobrenome"
                            value={props.scope.lastName}
                            error={props.scope.lastNameError}
                            onChange={(value) => props.scope.onLastNameChanged(value)}
                        />
                        <FormField
                            label="Nome social"
                            value={props.scope.socialName}
                            onChange={(value) => props.scope.onSocialNameChanged(value)}
                        />
                        <FormField
                            label="Telefone"
                            value={props.scope.phone}
                            error={props.scope.phoneError}
                            onChange={(value) => props.scope.onPhoneChanged(value)}
                        />

                        <div>
                            <FormField
                                label="CPF"
                                value={props.scope.cpf}
                                error={props.scope.cpfError}
                                onChange={(value) => props.scope.onCpfChanged(value)}
                            />
                            {/*
                                O CPF fabricado a partir do CNPJ, marcado. Sem isto ele
                                passa por identidade de verdade - e o primeiro
                                administrador de TODA empresa tem um.
                            */}
                            <Show when={props.scope.cpfFicticio}>
                                <p class={`${Css.note} ${Css.caution}`}>
                                    Gerado a partir do CNPJ no cadastro da empresa. Não pertence a nenhuma pessoa.
                                </p>
                            </Show>
                        </div>
                    </div>

                    <Show when={props.scope.showRegistration}>
                        <p class={Css.section}>Atuação</p>

                        <div class={Css.grid}>
                            {/*
                                A PROFISSAO e desta empresa; o REGISTRO e da pessoa.

                                Sao coisas de donos diferentes lado a lado, e por isso
                                a nota: editar a profissao aqui nao mexe no que ela
                                exerce no outro cliente, e editar o numero do conselho
                                mexe - ele e emitido para ela, e nao para o contrato.
                            */}
                            <div>
                                <ChoiceFilter
                                    label="Profissão nesta empresa"
                                    summary={props.scope.typeSummary}
                                    placeholder="Selecione a profissão"
                                    hasValue={!!props.scope.typeSummary}
                                    onClear={() => void props.scope.onClearType()}
                                    options={props.scope.types.map((type) => ({
                                        label: type.label,
                                        value: type.value,
                                        current: type.current,
                                        onSelect: type.onSelect
                                    }))}
                                />
                                <p class={Css.note}>Em outra empresa ela pode exercer outra.</p>
                            </div>

                            {/*
                        A ESPECIALIDADE, que so existe para profissao que tem uma.
                        "Medico" tem cinquenta e cinco; personal trainer e fisiologista
                        nao tem nenhuma, e ai o seletor nao aparece.
                    */}
                            <Show when={props.scope.specialties.length > 0}>
                                <div>
                                    <ChoiceFilter
                                        label="Especialidade"
                                        summary={props.scope.specialtySummary}
                                        placeholder="Selecione a especialidade"
                                        hasValue={!!props.scope.specialtySummary}
                                        onClear={() => void props.scope.onClearSpecialty()}
                                        options={props.scope.specialties.map((type) => ({
                                            label: type.label,
                                            value: type.value,
                                            current: type.current,
                                            onSelect: type.onSelect
                                        }))}
                                    />
                                </div>
                            </Show>

                            {/*
                                O registro so aparece para conselho REGULAMENTADO: a
                                base traz 55 profissoes e uma delas e literalmente
                                `outro`. Cobrar um numero de quem nao tem conselho
                                seria um campo impossivel de preencher.
                            */}
                            <Show when={props.scope.regulated}>
                                <FormField
                                    label="Número do registro"
                                    placeholder="CRM, CRN, CREF…"
                                    value={props.scope.registrationNumber}
                                    onChange={(value) => props.scope.onRegistrationChanged(value)}
                                />
                                <FormField
                                    label="UF do registro"
                                    placeholder="SC"
                                    value={props.scope.uf}
                                    onChange={(value) => props.scope.onUfChanged(value)}
                                />
                            </Show>
                        </div>
                    </Show>

                    <Show when={props.scope.formError}>
                        {(message) => (
                            <p role="alert" class={Css.alert}>
                                {message()}
                            </p>
                        )}
                    </Show>

                    <div class={Css.footer}>
                        <Button type="submit" disabled={!props.scope.dirty || props.scope.saving}>
                            {props.scope.saving ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </div>
                </div>
            </Panel>
        </form>
    )
}

/**
 * Quem este profissional acompanha.
 *
 * E `patient_professionals`, o vinculo de ACESSO - e nao a relacao dele com a
 * empresa, que e administracao e nao da acesso a nada. Cada interruptor grava NA
 * HORA: sao decisoes independentes, e um "salvar" no fim daria a entender que a lista
 * inteira e uma coisa so.
 */
function Assignment(props: { scope: CompanyUserDetailScope }): JSX.Element {
    return (
        <Panel title="Colaboradores acompanhados">
            <Show when={!props.scope.assignError} fallback={<ErrorNotice message={props.scope.assignError!} />}>
                <div class={Css.panel}>
                    <div class={Css.searchRow}>
                        <TextFilter
                            label="Buscar"
                            placeholder="Nome ou CPF"
                            value={props.scope.term}
                            onChange={(value) => props.scope.onTermChanged(value)}
                        />
                        {/*
                            O contador olha a lista INTEIRA, e nao a filtrada: "3 de
                            40" mudaria de significado se o 40 encolhesse ao digitar.
                        */}
                        <p class={Css.count}>
                            Acompanha <strong class={Css.countDone}>{props.scope.assignedCount}</strong> de{' '}
                            <span class={Css.countTotal}>{props.scope.total}</span> colaboradores
                        </p>
                    </div>

                    <Show when={!props.scope.assignLoading} fallback={<Loading />}>
                        <Show
                            when={props.scope.rows.length > 0}
                            fallback={
                                <p class={Css.nobody}>
                                    {props.scope.total === 0
                                        ? 'Esta empresa ainda não tem colaboradores.'
                                        : 'Nenhum colaborador com esse termo.'}
                                </p>
                            }
                        >
                            <ul class={Css.list}>
                                <For each={props.scope.rows}>{(row) => <AssignRow scope={row} />}</For>
                            </ul>
                        </Show>
                    </Show>
                </div>
            </Show>
        </Panel>
    )
}

function AssignRow(props: { scope: AssignRowScope }): JSX.Element {
    return (
        <li class={Css.person}>
            <span class={Css.personTexts}>
                <span class={Css.personName}>{props.scope.name}</span>
                <span class={Css.personCpf}>{props.scope.cpf}</span>
            </span>

            {/*
                Um interruptor, e nao uma caixa de marcar: o estado vale NO SERVIDOR
                assim que muda, e o interruptor e o controle que diz isso.
            */}
            <button
                type="button"
                role="switch"
                aria-checked={props.scope.assigned}
                aria-label={`${props.scope.assigned ? 'Retirar' : 'Dar'} acesso a ${props.scope.name}`}
                disabled={props.scope.busy}
                onClick={() => void props.scope.onToggle()}
                class={Css.toggle}
                classList={{ [Css.on]: props.scope.assigned }}
            >
                <span class={Css.knob} />
            </button>
        </li>
    )
}

/**
 * As duas acoes sobre a conta, e elas NAO sao a mesma.
 *
 * Desativar tira o acesso e mantem a pessoa no quadro - e o caso de quem sai de
 * licenca. Desligar tira do quadro e corta os vinculos com os colaboradores, e nao se
 * desfaz com um clique: religar seria refazer as atribuicoes uma a uma.
 */
function Account(props: { scope: CompanyUserDetailScope }): JSX.Element {
    return (
        <Panel title="Acesso e vínculo">
            <div class={Css.panel}>
                {/*
                    Tornar master (B-30): so aparece para quem pode passar a condicao - o
                    master atual ou a plataforma - e so em administrador ativo.
                */}
                <Show when={props.scope.podeTornarMaster}>
                    <div class={`${Css.action} ${Css.dividedBelow}`}>
                        <div class={Css.actionTexts}>
                            <span class={Css.actionTitle}>Administrador master</span>
                            <span class={Css.actionText}>
                                Quem cadastra, desativa e desliga os outros administradores. A empresa tem no máximo um.
                            </span>
                        </div>

                        <Button
                            variant="outline"
                            disabled={props.scope.accountBusy}
                            onClick={() => void props.scope.onMakeMaster()}
                        >
                            Tornar master
                        </Button>
                    </div>
                </Show>

                {/*
                    Sem permissao para mexer no acesso, as duas acoes saem e fica o porque.
                    O servidor recusaria de qualquer jeito; oferecer e recusar so ensina a
                    regra depois do clique.
                */}
                <Show
                    when={props.scope.podeAlterarAcesso}
                    fallback={<p class={Css.actionText}>{props.scope.motivoSemAcesso}</p>}
                >
                    <div class={Css.action}>
                        <div class={Css.actionTexts}>
                            <span class={Css.actionTitle}>
                                {props.scope.active ? 'Acesso liberado' : 'Acesso suspenso'}
                            </span>
                            <span class={Css.actionText}>
                                {props.scope.active
                                    ? 'Esta pessoa entra no painel normalmente.'
                                    : 'Continua no quadro da empresa, mas não consegue entrar.'}
                            </span>
                        </div>

                        <Button
                            variant="outline"
                            disabled={props.scope.accountBusy}
                            onClick={() => void props.scope.onToggleActive()}
                        >
                            {props.scope.active ? 'Desativar acesso' : 'Reativar acesso'}
                        </Button>
                    </div>

                    <div class={`${Css.action} ${Css.divided}`}>
                        <div class={Css.actionTexts}>
                            <span class={Css.actionTitle}>Desligar da empresa</span>
                            <span class={Css.actionText}>Sai do quadro e perde os colaboradores acompanhados.</span>
                        </div>

                        <Button variant="danger" onClick={() => void props.scope.onRemove()}>
                            Desligar
                        </Button>
                    </div>
                </Show>
            </div>
        </Panel>
    )
}
